/**
 * MessagingProvider — single-WebSocket root for the WEB messaging surface.
 *
 * Why a Provider (not a bare hook)?
 * Previously each caller (`<MessagesBell />`, `<Messages />`) called the
 * `useMessaging` hook independently and each opened its own WebSocket. Any
 * navigation between `/app/messages` and `/app/messages/:convId` remounted
 * `<Messages />` (React Router treats each Route element as its own tree),
 * which tore down the socket and re-fetched the whole conversation list —
 * the user perceived this as a full page refresh.
 *
 * Mounting one provider at the AppShell level fixes that: the socket + list
 * live above the route boundary, so route changes stay pure client-side.
 *
 * Shape mirrors `use-messaging.jsx` verbatim so the migration is a swap-in.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from './use-auth';

const MessagingContext = createContext(null);

export const useMessaging = () => {
  const ctx = useContext(MessagingContext);
  if (!ctx) throw new Error('useMessaging must be used within <MessagingProvider>');
  return ctx;
};

export const newCmid = () => `cmid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const WS_PATH = '/api/ws/messaging';
const toWs = (base) => base?.replace(/^http/i, (m) => (m.toLowerCase() === 'https' ? 'wss' : 'ws'));

export const MessagingProvider = ({ children }) => {
  const { authedFetch, token, API, user } = useAuth();
  const [connectionState, setConnectionState] = useState('closed');
  // Sidebar state — kept HERE (above route boundary) so `<Messages/>` never
  // re-mounts into an empty list when navigating between /app/messages/*
  // URLs. Also survives brief AuthContext re-renders that used to blank
  // the sidebar for a beat.
  const [conversations, setConversations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [listLoaded, setListLoaded] = useState(false);
  // Presence — user_id -> 'online'|'offline'. Updated by WS `presence.updated`
  // events and by poll-fallback snapshot fetches from Messages.jsx. Reads
  // are frame-cheap; only DM rows + chat headers subscribe.
  const [presenceMap, setPresenceMap] = useState({});
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const backoffRef = useRef(1000);
  const closingRef = useRef(false);

  const call = useCallback(async (path, opts = {}) => {
    const res = await authedFetch(path, opts);
    const text = await res.text().catch(() => '');
    let json;
    try { json = text ? JSON.parse(text) : {}; } catch { json = {}; }
    if (!res.ok) {
      const err = new Error(json?.detail || text || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return json;
  }, [authedFetch]);

  const subscribe = useCallback((handler) => {
    listenersRef.current.add(handler);
    return () => listenersRef.current.delete(handler);
  }, []);

  const emit = useCallback((event) => {
    listenersRef.current.forEach((fn) => { try { fn(event); } catch { /* ignore */ } });
  }, []);

  useEffect(() => {
    if (!token || !API) {
      try { wsRef.current && wsRef.current.close(); } catch { /* ignore */ }
      wsRef.current = null;
      setConnectionState('closed');
      return () => {};
    }
    const wsBase = toWs(API);
    if (!wsBase) return () => {};
    closingRef.current = false;

    let cancelled = false;
    let ws;
    let reconnectTimer = null;
    let heartbeatId = null;

    const openSocket = () => {
      if (cancelled) return;
      setConnectionState('connecting');
      ws = new WebSocket(`${wsBase}${WS_PATH}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnectionState('open');
        backoffRef.current = 1000;
        heartbeatId = setInterval(() => {
          try { ws.send(JSON.stringify({ type: 'heartbeat' })); } catch { /* ignore */ }
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try { emit(JSON.parse(ev.data)); } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setConnectionState('closed');
        if (heartbeatId) clearInterval(heartbeatId);
        if (closingRef.current || cancelled) return;
        const delay = Math.min(backoffRef.current, 15000);
        backoffRef.current = Math.min(backoffRef.current * 2, 15000);
        reconnectTimer = setTimeout(openSocket, delay);
      };
      ws.onerror = () => { /* handled by onclose */ };
    };

    openSocket();

    return () => {
      cancelled = true;
      closingRef.current = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (heartbeatId) clearInterval(heartbeatId);
      try { ws && ws.close(); } catch { /* ignore */ }
    };
  }, [token, API, emit]);

  const sendTypingOverWs = useCallback((conversationId, isTyping) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: 'typing', conversation_id: conversationId, is_typing: !!isTyping }));
        return;
      } catch { /* fall through to REST */ }
    }
    // WS is closed → fall back to the REST typing endpoint so the
    // recipient still sees "typing…" through their poll snapshot.
    // Fire-and-forget.
    authedFetch(`/api/messaging/conversations/${encodeURIComponent(conversationId)}/typing`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_typing: !!isTyping }),
    }).catch(() => null);
  }, [authedFetch]);

  // Route the `call` helper through a ref so the outward-facing API functions
  // can be memoised ONCE (dep-free) — otherwise every WebSocket state flip
  // (`connecting` → `open`) would create new function references and cascade
  // into every consumer's useEffect, producing the "Loading…" flicker on the
  // conversation list.
  const callRef = useRef(call);
  useEffect(() => { callRef.current = call; }, [call]);

  // ── Sidebar list refresh ─────────────────────────────────────────────
  // Lives HERE so state persists across `<Messages/>` mount cycles.
  //
  // ⚠️ Identity preservation matters: when we replace `conversations` with
  // a fresh array on every WS tick, every `<ConversationRow />` re-renders
  // because its `conv` prop is a new object even when nothing changed.
  // That causes the "sidebar reloads on every chat switch" flash the user
  // reported after M4. Merge by id and reuse the previous object reference
  // for any row whose serialised shape is identical.
  const _convFingerprint = (c) => JSON.stringify({
    id: c.id, title: c.title, avatar: c.avatar, last_message_at: c.last_message_at,
    unread: c.unread, muted_until: c.muted_until, favorited_at: c.favorited_at,
    dm_status: c.dm_status, is_archived: c.is_archived, my_role: c.my_role,
    other_user: c.other_user,
  });
  const refreshList = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        callRef.current('/api/messaging/conversations'),
        callRef.current('/api/messaging/requests'),
      ]);
      const next = c?.conversations || [];
      setConversations((prev) => {
        const prevById = new Map(prev.map((x) => [x.id, x]));
        // Merge: reuse the previous ref when the fingerprint matches,
        // otherwise take the new row. Preserves the LIST array identity
        // only when EVERY row is stable — which is what we want, because
        // a truly-unchanged list should not trigger any re-render.
        let dirty = next.length !== prev.length;
        const merged = next.map((n) => {
          const old = prevById.get(n.id);
          if (old && _convFingerprint(old) === _convFingerprint(n)) return old;
          dirty = true;
          return n;
        });
        return dirty ? merged : prev;
      });
      setRequests(r?.requests || []);
    } catch { /* stay on stale cache */ }
    finally { setListLoaded(true); }
  }, []);

  // Boot the list ONCE per authenticated user session.
  useEffect(() => {
    if (!user?.id || !token) {
      setConversations([]); setRequests([]); setListLoaded(false);
      return;
    }
    refreshList();
  }, [user?.id, token, refreshList]);

  // Global presence pull — runs on mount + every 30s regardless of the
  // WS state. `presence.updated` WS events only fire on connect/disconnect
  // of the OTHER user, so peers who were already online when we
  // connected wouldn't otherwise appear until they cycle. This bulk
  // snapshot fills the gap for the sidebar green dots.
  useEffect(() => {
    if (!token || !user?.id) return () => {};
    let cancelled = false;
    const pull = async () => {
      try {
        const p = await callRef.current('/api/messaging/presence').catch(() => null);
        if (cancelled || !p || !Array.isArray(p.online_user_ids)) return;
        const online = new Set(p.online_user_ids);
        setPresenceMap((prev) => {
          // Rewrite the entire map so users who went offline flip to
          // offline in the same pass. Safe: this endpoint is authoritative.
          const next = {};
          for (const id of online) next[id] = 'online';
          // Preserve keys we had previously seen (as offline) so the UI
          // doesn't flicker; only overwrite the online ones.
          for (const key of Object.keys(prev)) {
            if (!next[key]) next[key] = 'offline';
          }
          return next;
        });
      } catch { /* silent */ }
    };
    pull();
    const iv = setInterval(pull, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [token, user?.id]);

  // Polling fallback — some production ingress setups reject the WS
  // upgrade (HTTP 403 on the /api/ws/messaging handshake). When that
  // happens `connectionState` stays 'closed' after the initial retry.
  // We then flip to a short-interval poller so the UI still feels
  // realtime (worst-case ~4s latency). The provider emits a synthetic
  // `poll.tick` event so consumers (like `Messages.jsx`) can re-fetch
  // the currently-open conversation's messages.
  useEffect(() => {
    if (!token || !user?.id) return () => {};
    if (connectionState === 'open') return () => {};
    const iv = setInterval(() => {
      refreshList();
      emit({ type: 'poll.tick', at: Date.now() });
    }, 4000);
    return () => clearInterval(iv);
  }, [connectionState, token, user?.id, refreshList, emit]);


  // WS event → keep the list fresh. Every message/read/conversation event
  // triggers a debounced refresh so unread counts + timestamps stay live.
  // WS event → keep the list fresh. `message.created` + conversation
  // mutations bump the sidebar (unread, order, title). `read.updated`
  // ONLY needs to bump the caller's own unread counter — the debounced
  // refresh at 400ms coalesces bursts (e.g. rapid chat switching where
  // markRead fires N times in a row).
  const refreshTimer = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => refreshList(), 400);
  }, [refreshList]);

  useEffect(() => {
    const unsub = ((h) => {
      listenersRef.current.add(h);
      return () => listenersRef.current.delete(h);
    })((evt) => {
      if (!evt) return;
      // `presence.updated` — server broadcasts on connect/disconnect.
      if (evt.type === 'presence.updated' && evt.user_id) {
        setPresenceMap((prev) => (
          prev[evt.user_id] === evt.status ? prev : { ...prev, [evt.user_id]: evt.status }
        ));
        return;
      }
      // `read.updated` is triggered by the CALLER's own markRead. Handle it
      // in-place instead of a full sidebar refetch — that was the "reload
      // flash on every chat switch" the user reported.
      if (evt.type === 'read.updated' && evt.conversation_id && evt.user_id === user?.id) {
        setConversations((prev) => prev.map((c) => (
          c.id === evt.conversation_id ? { ...c, unread: 0 } : c
        )));
        return;
      }
      if (['message.created', 'conversation.updated', 'conversation.deleted', 'conversation.created'].includes(evt.type)) {
        // Toast on the SENDER side when their pending DM was auto-accepted
        // by the recipient's first reply. Guard: only when the current user
        // is the conversation owner (i.e. the requester).
        if (evt.type === 'conversation.updated' && evt.reason === 'dm_auto_accepted'
            && evt.conversation?.owner_id === user?.id) {
          toast.success('Your request was accepted', {
            description: 'Their first reply auto-approved the DM.',
          });
        }
        scheduleRefresh();
      }
    });
    return unsub;
  }, [scheduleRefresh, user?.id]);

  const api = useMemo(() => {
    const c = (path, opts) => callRef.current(path, opts);
    return {
      subscribe, sendTypingOverWs,
      listConversations: () => c('/api/messaging/conversations'),
      createConversation: (body) => c('/api/messaging/conversations', { method: 'POST', body: JSON.stringify(body) }),
      getConversation: (id) => c(`/api/messaging/conversations/${encodeURIComponent(id)}`),
      patchConversation: (id, patch) => c(`/api/messaging/conversations/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      acceptRequest: (id) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/accept`, { method: 'POST' }),
      declineRequest: (id) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/decline`, { method: 'POST' }),
      listMessages: (convId, { before, limit = 30 } = {}) => {
        const qs = new URLSearchParams();
        if (before) qs.set('before', before);
        if (limit) qs.set('limit', String(limit));
        return c(`/api/messaging/conversations/${encodeURIComponent(convId)}/messages?${qs.toString()}`);
      },
      sendMessage: (convId, body, replyTo, cmid, attachments, mentions) => c(`/api/messaging/conversations/${encodeURIComponent(convId)}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          body: body || '',
          reply_to: replyTo || null,
          client_message_id: cmid,
          ...(attachments && attachments.length ? { attachments } : {}),
          ...(mentions && mentions.length ? { mentions } : {}),
        }),
      }),
      editMessage: (msgId, body) => c(`/api/messaging/messages/${encodeURIComponent(msgId)}`, { method: 'PATCH', body: JSON.stringify({ body }) }),
      deleteMessage: (msgId) => c(`/api/messaging/messages/${encodeURIComponent(msgId)}`, { method: 'DELETE' }),
      markRead: (convId) => c(`/api/messaging/conversations/${encodeURIComponent(convId)}/read`, { method: 'POST' }),
      addReaction: (msgId, emoji) => c(`/api/messaging/messages/${encodeURIComponent(msgId)}/reactions`, { method: 'POST', body: JSON.stringify({ emoji }) }),
      removeReaction: (msgId, emoji) => c(`/api/messaging/messages/${encodeURIComponent(msgId)}/reactions/${encodeURIComponent(emoji)}`, { method: 'DELETE' }),
      getReactionDetail: (msgId) => c(`/api/messaging/messages/${encodeURIComponent(msgId)}/reactions/detail`),
      pinMessage: (msgId) => c(`/api/messaging/messages/${encodeURIComponent(msgId)}/pin`, { method: 'POST' }),
      unpinMessage: (msgId) => c(`/api/messaging/messages/${encodeURIComponent(msgId)}/pin`, { method: 'DELETE' }),
      listPinned: (convId) => c(`/api/messaging/conversations/${encodeURIComponent(convId)}/pinned`),
      sendTyping: (convId, isTyping) => c(`/api/messaging/conversations/${encodeURIComponent(convId)}/typing`, {
        method: 'POST', body: JSON.stringify({ is_typing: !!isTyping }),
      }).catch(() => null),
      listRequests: () => c('/api/messaging/requests'),
      searchUsers: (q) => c(`/api/messaging/users/search?q=${encodeURIComponent(q)}`),
      getUserProfile: (id) => c(`/api/messaging/users/${encodeURIComponent(id)}`).catch(() => null),
      // Poll-fallback: snapshot currently-typing user ids for a conv.
      getTypingSnapshot: (convId) => c(`/api/messaging/conversations/${encodeURIComponent(convId)}/typing`).catch(() => ({ typing_user_ids: [] })),
      // Poll-fallback: snapshot currently-online member ids for a conv.
      getPresenceSnapshot: (convId) => c(`/api/messaging/conversations/${encodeURIComponent(convId)}/presence`).catch(() => ({ online_user_ids: [] })),
      // Aggregate presence across ALL of the caller's conversations —
      // used by the sidebar so green dots render on rows without
      // opening each chat first.
      getGlobalPresence: () => c('/api/messaging/presence').catch(() => ({ online_user_ids: [] })),
      // Self-mute a conversation for the current user. `until` is an
      // ISO-8601 string (or null to un-mute). Not to be confused with
      // `moderatorMute` which mutes OTHER members.
      muteConversation: (id, until) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/mute`, {
        method: 'POST', body: JSON.stringify({ until: until || null }),
      }),
      // Discard an owner-created conversation with zero messages. Used when
      // a user opens a fresh DM but never sends anything.
      discardConversation: (id) => c(`/api/messaging/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' }).catch(() => null),
      // ── M3: groups + user controls ────────────────────────────────
      addMembers: (id, user_ids) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/members`, {
        method: 'POST', body: JSON.stringify({ user_ids }),
      }),
      removeMember: (id, uid) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(uid)}`, { method: 'DELETE' }),
      leaveConversation: (id) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/leave`, { method: 'POST' }),
      changeMemberRole: (id, uid, role) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(uid)}/role`, {
        method: 'PATCH', body: JSON.stringify({ role }),
      }),
      transferOwnership: (id, new_owner_id) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/transfer-ownership`, {
        method: 'POST', body: JSON.stringify({ new_owner_id }),
      }),
      moderatorMute: (id, uid, until) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(uid)}/mute`, {
        method: 'POST', body: JSON.stringify({ until: until || null }),
      }),
      restrictMember: (id, uid) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(uid)}/restrict`, { method: 'POST' }),
      unrestrictMember: (id, uid) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(uid)}/unrestrict`, { method: 'POST' }),
      banMember: (id, uid) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(uid)}/ban`, { method: 'POST' }),
      unbanMember: (id, uid) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(uid)}/unban`, { method: 'POST' }),
      archiveConversation: (id, is_archived) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/archive`, {
        method: 'POST', body: JSON.stringify({ is_archived }),
      }),
      updateConversation: (id, patch) => c(`/api/messaging/conversations/${encodeURIComponent(id)}`, {
        method: 'PATCH', body: JSON.stringify(patch),
      }),
      listAudit: (id, limit = 50) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/audit?limit=${limit}`),
      // ── M4: attachments · search · report · block · favourite ─────
      presignAttachment: (convId, { filename, contentType, size }) => c(
        `/api/messaging/conversations/${encodeURIComponent(convId)}/attachments/presign`,
        { method: 'POST',
          body: JSON.stringify({ filename, content_type: contentType, size }) },
      ),
      searchMessages: (convId, q, { cursor, limit = 15 } = {}) => {
        const qs = new URLSearchParams({ q });
        if (cursor) qs.set('cursor', cursor);
        if (limit) qs.set('limit', String(limit));
        return c(`/api/messaging/conversations/${encodeURIComponent(convId)}/search?${qs.toString()}`);
      },
      reportMessage: (msgId, reason, notes) => c(
        `/api/messaging/messages/${encodeURIComponent(msgId)}/report`,
        { method: 'POST', body: JSON.stringify({ reason, notes: notes || null }) },
      ),
      listBlocks: () => c('/api/messaging/blocks'),
      blockUser: (targetId) => c(`/api/messaging/users/${encodeURIComponent(targetId)}/block`, { method: 'POST' }),
      unblockUser: (targetId) => c(`/api/messaging/users/${encodeURIComponent(targetId)}/block`, { method: 'DELETE' }),
      favoriteConversation: (id) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/favorite`, { method: 'POST' }),
      unfavoriteConversation: (id) => c(`/api/messaging/conversations/${encodeURIComponent(id)}/favorite`, { method: 'DELETE' }),
      refreshList,
    };
  }, [subscribe, sendTypingOverWs, refreshList]);

  // Connection state + sidebar caches piggy-back on the stable `api` so
  // consumer memos that only read the methods stay stable.
  const bulkSetPresence = useCallback((online_ids = []) => {
    setPresenceMap((prev) => {
      // Any id in `online_ids` = online; anyone already tracked but not in
      // the list = keep whatever we had (avoid flapping to offline just
      // because the caller only asked about one conversation's members).
      let dirty = false;
      const next = { ...prev };
      for (const uid of online_ids) {
        if (next[uid] !== 'online') { next[uid] = 'online'; dirty = true; }
      }
      return dirty ? next : prev;
    });
  }, []);

  const value = useMemo(() => ({
    ...api, connectionState, conversations, requests, listLoaded,
    presenceMap, bulkSetPresence,
  }), [api, connectionState, conversations, requests, listLoaded, presenceMap, bulkSetPresence]);

  return <MessagingContext.Provider value={value}>{children}</MessagingContext.Provider>;
};
